<script setup>
import { useResizeObserver } from '@vueuse/core'
import { cloneDeep, isEmpty, isNull, isUndefined, floor } from 'lodash'
import { computed, handleError, nextTick, ref, shallowRef, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import OkrFiltersApiHandler from '@/api/okr-filters'
import { showNotify } from '@/utils/notify'
import { numberOrNullProp } from '@/utils/prop-validators'

import AppDivider from '@/components/ui/AppDivider'
import CheckinFeedCustomFilterModal from '@/views/check-ins/CheckinFeedCustomFilterModal'
import CheckinFiltersDropdown from '@/views/check-ins/CheckinFiltersDropdown'
import CheckinsFeedFilterButton from '@/views/check-ins/CheckinsFeedFilterButton'
import {
  CHECKIN_FEED_TOOLBAR_ADD_FILTER_BUTTON_TEST_ID,
  CHECKIN_FEED_TOOLBAR_FILTER_BUTTON_TEST_ID,
  CHECKIN_FEED_TOOLBAR_FILTER_BUTTON_TEST_ID_ELEMENT,
  CHECKIN_FEED_TOOLBAR_FILTERS_LIST_TEST_ID
} from '@/views/check-ins/jest-helpers'

defineOptions({
  name: 'CheckinsFeedToolbar'
})

const props = defineProps({
  savedFilters: {
    type: Array,
    default: () => []
  },

  hideFilters: {
    type: Boolean,
    default: false
  }
})

const model = defineModel({
  validator: v => numberOrNullProp(v),
  set: v => v
})

const emit = defineEmits({
  'update-filters': filters => Array.isArray(filters),
  'reload-filters': null,
  'filter-applied': null,
  'clear-filters': null,
  'on-filter-created': null,
  'on-filter-updated': null
})

const onFilterClick = filter => {
  if (!filter || filter.id === model.value) {
    clearFilters()
    return
  }
  model.value = filter.id
  emit('filter-applied', filter)
}

const filterForEdit = shallowRef(null)

const onEditFilter = filter => {
  filterForEdit.value = filter
  openCustomFilterModal()
}

const clearFilters = () => {
  if (isNull(model.value)) return
  emit('clear-filters')
}

const isLoading = ref(false)
const { t } = useI18n()

const onRemoveFilter = async filter => {
  if (!filter) return

  const api = new OkrFiltersApiHandler()
  isLoading.value = true

  try {
    const { id, name } = filter
    await api.deleteFilterView(id)

    showNotify({
      title: t('filters.filter_deleted', { filterName: name })
    })

    if (model.value === id) {
      clearFilters()
    }

    emit('reload-filters')

    closeCustomFilterModal()
  } catch (error) {
    handleError({ error })
  } finally {
    isLoading.value = false
  }
}

const toolbarWrapperReference = useTemplateRef('toolbar-wrapper')
const toolbarActionsReference = useTemplateRef('toolbar-actions')

useResizeObserver([toolbarActionsReference, toolbarWrapperReference], () => {
  updateLayout()
})

const showOtherFiltersTrigger = ref(true)

const filtersThatFit = ref([])
const filtersThatDoNotFit = ref([])
const filtersListReference = useTemplateRef('filters-list')
const filtersButtonsReferences = ref({})

const setFilterButtonReference = (element, index) => {
  if (element) {
    filtersButtonsReferences.value[index] = element
  }
}

const isLayoutUpdating = ref(false)

const updateLayout = async () => {
  // extra guard for issue OK-4486
  isLayoutUpdating.value = true
  filtersThatFit.value = cloneDeep(props.savedFilters)
  filtersThatDoNotFit.value = []
  filtersButtonsReferences.value = {}

  await nextTick()

  const filtersWrapper = filtersListReference.value
  if (!filtersWrapper) {
    isLayoutUpdating.value = false
    return
  }

  for (let i = props.savedFilters.length - 1; i >= 0; i--) {
    await nextTick()

    const button = filtersButtonsReferences.value[i]

    const buttonRight = button?.getBoundingClientRect().right || 0
    const wrapperRight = filtersWrapper.getBoundingClientRect().right || 0

    // Using floor to avoid floating point precision issues with sub-pixel values OK-4486
    let isButtonInView = floor(buttonRight, 2) <= floor(wrapperRight, 2)

    await nextTick()

    if (isButtonInView) {
      filtersThatDoNotFit.value = filtersThatFit.value.splice(
        i + 1,
        filtersThatFit.value.length - 1
      )

      break
    }

    if (i === 0) {
      filtersThatDoNotFit.value = filtersThatFit.value.splice(0, filtersThatFit.value.length)

      break
    }
  }

  await nextTick()

  showOtherFiltersTrigger.value = !isEmpty(filtersThatDoNotFit.value)
  isLayoutUpdating.value = false
}

watch(
  () => props.savedFilters,
  newValue => {
    if (!isUndefined(newValue)) {
      updateLayout()
    }
  },
  { immediate: true, deep: true }
)

const isShowOtherFiltersDropdown = ref(false)

const isFilterThatDoNotFitSelected = computed(() => {
  return !isNull(model.value) && filtersThatDoNotFit.value.some(f => f.id === model.value)
})

const isNoSavedFilters = computed(() => isEmpty(props.savedFilters))

const isShowCustomFilterModal = ref(false)

const openCustomFilterModal = () => {
  isShowCustomFilterModal.value = true
}

const closeCustomFilterModal = () => {
  if (!isShowCustomFilterModal.value) return

  isShowCustomFilterModal.value = false
  filterForEdit.value = null
}
</script>

<template>
  <div ref="toolbar-wrapper" class="cft-Toolbar">
    <CheckinsFeedFilterButton
      v-if="!hideFilters"
      :active="isNull(model)"
      class="сfb-Button-with-dimensions"
      @click="clearFilters"
    >
      {{ $t('common.all') }}
    </CheckinsFeedFilterButton>

    <AppDivider v-if="!hideFilters" class="cft-Toolbar_Divider" vertical />

    <template v-if="!isNoSavedFilters && !hideFilters">
      <div
        v-show="!isEmpty(filtersThatFit)"
        ref="filters-list"
        :data-auto-testid="CHECKIN_FEED_TOOLBAR_FILTERS_LIST_TEST_ID"
        :data-testid="CHECKIN_FEED_TOOLBAR_FILTERS_LIST_TEST_ID"
        class="cft-Toolbar_Filters"
      >
        <div
          v-for="(filter, index) in filtersThatFit"
          :key="filter.id"
          :ref="b => setFilterButtonReference(b, index)"
          :data-auto-testid="CHECKIN_FEED_TOOLBAR_FILTER_BUTTON_TEST_ID"
          :data-testid="CHECKIN_FEED_TOOLBAR_FILTER_BUTTON_TEST_ID"
          class="cft-ButtonWrapper"
        >
          <CheckinsFeedFilterButton
            :active="model === filter.id"
            :data-auto-testid="CHECKIN_FEED_TOOLBAR_FILTER_BUTTON_TEST_ID_ELEMENT"
            :data-testid="CHECKIN_FEED_TOOLBAR_FILTER_BUTTON_TEST_ID_ELEMENT"
            class="сfb-Button-with-dimensions"
            editable
            @click="onFilterClick(filter)"
            @edit="onEditFilter(filter)"
          >
            {{ filter.name }}
          </CheckinsFeedFilterButton>
        </div>
      </div>

      <CheckinFiltersDropdown
        v-if="showOtherFiltersTrigger"
        v-model="isShowOtherFiltersDropdown"
        :active-filter-id="model"
        :filters="savedFilters"
        :hide-content="isLayoutUpdating"
        :is-trigger-active="isFilterThatDoNotFitSelected"
        :loading="isLoading"
        @update-filters="emit('update-filters', $event)"
        @reload-filters="emit('reload-filters')"
        @apply-filter="onFilterClick"
        @edit-filter="onEditFilter"
        @remove-filter="onRemoveFilter"
      />
    </template>

    <CheckinsFeedFilterButton
      v-if="isNoSavedFilters && !hideFilters"
      :active="isShowCustomFilterModal"
      :data-auto-testid="CHECKIN_FEED_TOOLBAR_ADD_FILTER_BUTTON_TEST_ID"
      :data-testid="CHECKIN_FEED_TOOLBAR_ADD_FILTER_BUTTON_TEST_ID"
      height="24"
      icon="plus-next"
      width="24"
      @click="openCustomFilterModal"
    >
      {{ t('schedule.participants.add_filter') }}
    </CheckinsFeedFilterButton>

    <CheckinsFeedFilterButton
      v-else-if="!hideFilters"
      v-tippy="{
        content: isNoSavedFilters ? null : t('schedule.participants.add_filter')
      }"
      :active="isShowCustomFilterModal"
      :data-auto-testid="CHECKIN_FEED_TOOLBAR_ADD_FILTER_BUTTON_TEST_ID"
      :data-testid="CHECKIN_FEED_TOOLBAR_ADD_FILTER_BUTTON_TEST_ID"
      height="24"
      icon="plus-next"
      remove-padding
      width="24"
      @click="openCustomFilterModal"
    />

    <div ref="toolbar-actions" class="cft-Toolbar_Actions">
      <slot name="toolbar-actions" />
    </div>

    <portal v-if="!hideFilters" to="modal-windows">
      <CheckinFeedCustomFilterModal
        :filter-data="filterForEdit"
        :opened="isShowCustomFilterModal"
        @on-close="closeCustomFilterModal"
        @remove-filter="onRemoveFilter"
        @on-filter-created="emit('on-filter-created', $event)"
        @on-filter-updated="emit('on-filter-updated', $event)"
      />
    </portal>
  </div>
</template>

<style lang="scss" scoped>
.cft-Toolbar {
  padding-top: var(--ob-cft-padding-top, #{$page-top-padding});
  align-items: flex-start;
  display: flex;
  gap: 8px;
  height: 100%;
}

.cft-Toolbar_Filters {
  display: flex;
  flex-wrap: nowrap;
  overflow: hidden;
  align-items: center;
  gap: inherit;
}

.cft-Toolbar_Actions {
  padding-left: 24px;
  margin-left: auto;
  flex-shrink: 0;
}

.cft-Toolbar_Divider {
  align-self: unset;
  min-height: unset;
  max-height: 32px;
}

.сfb-Button-with-dimensions {
  text-align: center;
  min-width: 36px;
  max-width: 140px;
}
</style>
